/**
 * Client for the consent-evidence endpoints (feat-022-consent-evidence).
 *
 * Contracts (authoritative):
 *  - Spec: worktrees/feat-022-consent-evidence/spec.md (D5, D7, D8, D9)
 *  - ADR 0001 §6 (withdraw-consent), §8 (consent-report)
 *
 * Both endpoints are RBAC-guarded server-side (check_tenant_permission +
 * check_location_permission). All timestamps in the report are UTC ISO-8601
 * with a `Z` suffix; the frontend localizes to the location timezone for the
 * PDF via `utils/locationTime`. Auth headers are passed in by the caller
 * (TenantDashboard already owns token resolution via its `getAuthHeaders`).
 */
import { fetchApi } from '../utils/api';

// ---------- Report payload (mirrors feat-024 ADR 0001 §D1–D3) ----------

/**
 * One `server_event` row in a server's evidentiary history. feat-024 broadened
 * this from consent-only to all six lifecycle `event_type`s (ADR D2) and renamed
 * the carrying field `consent_history` → `event_history` (ADR D1). The per-event
 * field set is frozen (ADR D3): no `ip`, `actor_id`, or actor email — the actor
 * is conveyed by `actor_type` + `source` only.
 */
export interface ServerEventEntry {
  event_type:
    | 'consent_given'
    | 'consent_withdrawn'
    | 'onboarding_complete'
    | 'onboarding_reset'
    | 'training_complete'
    | 'training_reset';
  occurred_at: string; // UTC ISO-8601 (Z)
  source: string; // 'app' | 'web' | 'backfill'
  actor_type: string; // 'server' | 'management' | 'system'
  consent_version: string | null;
  reason: string | null;
}

export interface ConsentReportServer {
  id: string;
  name: string;
  consent_given: boolean;
  consent_given_at: string | null; // UTC ISO-8601 (Z), or null
  onboarding_complete: boolean;
  training_complete: boolean;
  /**
   * Full chronological (ASC) `server_event` history, uncapped (feat-024 ADR D2).
   * Optional/undefined is treated as `[]` by the PDF builder — defensive against
   * an old backend and the zero-events case.
   */
  event_history?: ServerEventEntry[];
}

export interface ConsentReport {
  generated_at: string; // app UTC, ISO-8601 Z
  generated_by: { email: string; role: string };
  tenant: { id: string; name: string };
  location: { id: string; name: string; timezone: string | null };
  summary: { consented: number; total: number };
  servers: ConsentReportServer[];
}

export interface WithdrawConsentResult {
  success: boolean;
  /** true when consent was already withdrawn — idempotent no-op (ADR §6). */
  already_withdrawn?: boolean;
}

// ---------- Calls ----------

/**
 * GET /api/tenant/{tenantId}/locations/{locationId}/consent-report — the
 * evidentiary payload that backs the PDF (ADR §8). `generated_by` /
 * `generated_at` are stamped server-side from the token, not client-supplied.
 */
export function fetchConsentReport(
  tenantId: string,
  locationId: string,
  authHeaders: Record<string, string>,
): Promise<ConsentReport> {
  return fetchApi<ConsentReport>(
    `/api/tenant/${tenantId}/locations/${locationId}/consent-report`,
    { method: 'GET', headers: authHeaders },
  );
}

/**
 * POST /api/tenant/{tenantId}/locations/{locationId}/servers/{serverId}/withdraw-consent
 * (ADR §6). Atomically clears consent + resets onboarding and appends the
 * audit events server-side. `reason` is mandatory. Idempotent if already
 * withdrawn.
 */
export function withdrawConsent(
  tenantId: string,
  locationId: string,
  serverId: string,
  reason: string,
  authHeaders: Record<string, string>,
): Promise<WithdrawConsentResult> {
  return fetchApi<WithdrawConsentResult>(
    `/api/tenant/${tenantId}/locations/${locationId}/servers/${serverId}/withdraw-consent`,
    { method: 'POST', headers: authHeaders, body: { reason } },
  );
}
